import React, { useState, useRef, useEffect, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import AssignedAgentDisplay from "@/modules/common/components/assigned-agent-display";
import StarRating from "@/modules/common/components/star-rating";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import Status from "@/modules/common/components/ui-kit/status/status";
import { ActionsDropdown } from "@/modules/lead-page/new/components/header/actions/actions-drop-down";
import { UserV2 } from "@/modules/user/dtos/index.v2";
import {
  getRatingActions,
  getRatingTriggerButton,
} from "@/modules/lead-page/new/components/ratings/rating-card/rating-actions";
import {
  FollowUpStatusEnum,
  FollowUpStatusColors,
} from "@/modules/lead-page/enums";
import AddComment from "@/modules/lead-page/new/components/ratings/rating-card/add-comment";
import RatingComment from "@/modules/lead-page/new/components/ratings/rating-card/rating-card-comment";
import RatingThreadDrawer from "@/modules/lead-page/new/components/ratings/rating-thread/rating-thread-drawer";
import ResolveRatingModal from "@/modules/lead-page/new/components/ratings/rating-card/resolve-rating-modal";
import { useAddRatingComment } from "@/modules/lead-page/new/hooks/ratings/add-rating-comment.hook";
import { useRatingDetails } from "@/modules/lead-page/new/hooks/ratings/use-rating-details.hook";
import { ArrowRight } from "lucide-react";
import { capitalizeFirstLetter } from "@/modules/common/utils";
import { formatDateReadable } from "@/modules/leads/utils/index";
import { RatingCardProps, RatingCommentData } from "@/modules/lead-page/types";
import { isFollowUpResolved } from "@/modules/lead-page/utils";

const RatingCard: React.FC<RatingCardProps> = ({
  id,
  ratingUId,
  agent,
  rating,
  comment,
  touchpoint,
  followUpStatus,
  createdAt,
  comments = [],
  findInActivity,
  onResolvedFollowUp,
  isInThread = false,
  isHighlighted = false,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);
  const [dropdownCloseHandler, setDropdownCloseHandler] = useState<
    (() => void) | null
  >(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const previousCommentsLengthRef = useRef<number>(comments?.length || 0);

  // Fetch rating details when drawer is open
  const {
    ratingDetails,
    refetch: refetchRatingDetails,
    isLoading: isLoadingRatingDetails,
  } = useRatingDetails({
    ratingUId,
    enabled: isDrawerOpen, // Only fetch when drawer is open
  });

  // Trigger fetch when drawer opens
  useEffect(() => {
    if (isDrawerOpen) {
      refetchRatingDetails();
    }
  }, [isDrawerOpen, refetchRatingDetails]);

  const { handleSendComment, isPending } = useAddRatingComment({
    ratingUId,
    onSuccess: () => {
      // Scroll to card after comment is added (only when not in thread)
      if (!isInThread && cardRef.current) {
        setTimeout(() => {
          cardRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "end",
          });
        }, 100);
      }
    },
  });

  // Scroll to card when comments change (only when not in thread)
  useEffect(() => {
    if (
      !isInThread &&
      comments &&
      comments.length > previousCommentsLengthRef.current &&
      cardRef.current
    ) {
      setTimeout(() => {
        cardRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }, 100);
    }
    previousCommentsLengthRef.current = comments?.length || 0;
  }, [comments, isInThread]);

  const ratingActions = useMemo(() => {
    return getRatingActions({
      findInActivity,
      onResolvedFollowUp,
      onResolveClick: () => setIsResolveModalOpen(true),
      isResolved: isFollowUpResolved(followUpStatus),
      onActionClick: dropdownCloseHandler || undefined,
    });
  }, [
    findInActivity,
    onResolvedFollowUp,
    followUpStatus,
    dropdownCloseHandler,
  ]);

  const triggerButton = getRatingTriggerButton;

  return (
    <Box
      id={id}
      ref={cardRef}
      sx={{
        backgroundColor: nawyTheme.nawy.colors.primitives.white[100],
        borderRadius: nawyTheme.nawy.radius.lg,
        padding: `${nawyTheme.nawy.spacing.md} ${nawyTheme.nawy.spacing.md}`,
        display: "flex",
        flexDirection: "column",
        gap: isInThread
          ? nawyTheme.nawy.spacing.md
          : nawyTheme.nawy.spacing.xxs,
        transition: "background-color 0.3s ease-out",
        ...(isHighlighted && {
          backgroundColor: nawyTheme.nawy.colors.primitives.blue[200],
          animation: "highlightFlash 1.2s ease-in-out 3",
          "@keyframes highlightFlash": {
            "0%, 100%": { opacity: 1 },
            "50%": { opacity: 0.75 },
          },
        }),
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <AssignedAgentDisplay
          user={agent}
          withRank={true}
          agentNameSx={{
            ...nawyTheme.nawy.typography.english.variants.body.md.regular,
            color: nawyTheme.nawy.colors.semantic.text.primary,
          }}
          isMappingRank={true}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {followUpStatus &&
            followUpStatus !== FollowUpStatusEnum.NO_FOLLOW_UP && (
              <Status
                variant="secondary"
                label={capitalizeFirstLetter(followUpStatus)}
                color={FollowUpStatusColors[followUpStatus]}
                size="sm"
                textStyle={{
                  ...nawyTheme.nawy.typography.english.variants.body.xs.regular,
                }}
                width="fit-content"
              />
            )}
          {!isInThread && ratingActions.length > 0 && (
            <Box
              onClick={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              <ActionsDropdown
                triggerButton={triggerButton}
                items={ratingActions}
                onActionClick={(closeHandler) =>
                  setDropdownCloseHandler(() => closeHandler)
                }
                popperSx={{ zIndex: 1400 }}
              />
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: isInThread ? nawyTheme.nawy.spacing.xxs : 0,
        }}
      >
        {rating !== undefined && (
          <Box sx={{ marginLeft: isInThread ? "-2px" : 0 }}>
            <StarRating
              rating={rating}
              size="medium"
              filledColor={nawyTheme.nawy.colors.semantic.text.accent}
            />
          </Box>
        )}
        {comment && (
          <Typography
            sx={{
              ...nawyTheme.nawy.typography.english.variants.body.md.regular,
              color: nawyTheme.nawy.colors.semantic.text.primary,
            }}
          >
            {capitalizeFirstLetter(comment)}
          </Typography>
        )}
        {touchpoint && (
          <Typography
            sx={{
              ...nawyTheme.nawy.typography.english.variants.body.sm.regular,
              color: nawyTheme.nawy.colors.semantic.text.primary,
            }}
          >
            Touchpoint:{" "}
            <span
              style={{
                ...nawyTheme.nawy.typography.english.variants.body.sm.semibold,
                color: nawyTheme.nawy.colors.semantic.text.primary,
              }}
            >
              {touchpoint}
            </span>
          </Typography>
        )}
        {createdAt && (
          <Typography
            sx={{
              ...nawyTheme.nawy.typography.english.variants.body.xs.regular,
              color: nawyTheme.nawy.colors.semantic.text.secondary,
            }}
          >
            {formatDateReadable(createdAt as string)}
          </Typography>
        )}
      </Box>

      {/* Rating Comments */}
      {comments && comments.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: isInThread
              ? nawyTheme.nawy.spacing.lg
              : nawyTheme.nawy.spacing.xs,
            marginTop: nawyTheme.nawy.spacing.xs,
          }}
        >
          {(isInThread ? comments : comments.slice(0, 1)).map((commentItem) => (
            <RatingComment
              key={commentItem.uid || commentItem.id}
              comment={commentItem.comment}
              createdAt={commentItem.createdAt}
              user={commentItem.user as UserV2}
            />
          ))}
        </Box>
      )}

      {/* View Thread Link */}
      {!isInThread && (
        <Box
          onClick={(e) => {
            e.stopPropagation();
            setIsDrawerOpen(true);
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: nawyTheme.nawy.spacing.xs,
            cursor: "pointer",
            paddingTop: nawyTheme.nawy.spacing.xs,
            paddingBottom: nawyTheme.nawy.spacing.xs,
            width: "fit-content",
            "&:hover": {
              opacity: 0.8,
            },
          }}
        >
          <Typography
            sx={{
              ...nawyTheme.nawy.typography.english.variants.body.md.semibold,
              color: nawyTheme.nawy.colors.semantic.text.accent,
            }}
          >
            View thread
          </Typography>
          <ArrowRight
            size={16}
            color={nawyTheme.nawy.colors.semantic.text.accent as any}
          />
        </Box>
      )}

      {!isInThread && (
        <AddComment
          handleSendComment={handleSendComment}
          isPending={isPending}
        />
      )}

      {/* Rating Thread Drawer */}
      <RatingThreadDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        ratingUId={ratingUId}
        agent={ratingDetails?.agent as UserV2}
        rating={ratingDetails?.rating}
        comment={ratingDetails?.comment}
        touchpoint={ratingDetails?.status?.name}
        followUpStatus={ratingDetails?.followUpStatus as FollowUpStatusEnum}
        createdAt={ratingDetails?.createdAt}
        comments={ratingDetails?.comments as RatingCommentData[]}
        findInActivity={findInActivity}
        onResolvedFollowUp={() => {
          refetchRatingDetails();
          onResolvedFollowUp?.();
        }}
        isLoading={isLoadingRatingDetails}
      />

      {/* Resolve Rating Modal */}
      {onResolvedFollowUp && (
        <ResolveRatingModal
          open={isResolveModalOpen}
          setOpen={setIsResolveModalOpen}
          ratingUId={ratingUId}
          onSuccess={() => {
            onResolvedFollowUp();
          }}
          modalSx={{ zIndex: 1500 }}
        />
      )}
    </Box>
  );
};

export default RatingCard;
